import { useEffect, useState, type ReactNode } from "react";
import { Link } from "wouter";
import { CheckCircle2, ChevronRight, Clock, List, Plus, Sparkles, UserRound } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { isNativeApp } from "../lib/platform";

export type ReadState<T> = { scope: string; status: "loading" | "ready" | "error"; data: T | null };
export type Activity = { id: string; title: string; body: string; createdAt: string; link: string | null };
export type DashboardCompletion = { completeness: number; missing?: string[]; onboarded: boolean };
export type DashboardProfileSection = "overview" | "listings" | "activity" | "reviews";

function record(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null ? Object.fromEntries(Object.entries(value)) : {};
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export function notificationRows(value: unknown): Activity[] {
  const rows = record(value).notifications;
  if (!Array.isArray(rows)) throw new Error("Unavailable activity");
  return rows.flatMap((row: unknown) => {
    const item = record(row);
    if ((typeof item.id !== "string" && typeof item.id !== "number") || typeof item.title !== "string") return [];
    const destination = text(item.link);
    // Notification destinations are internal routes. Reject protocol-relative URLs and escapes.
    const safeLink = /^\/(?!\/)/.test(destination) && !/[\\\u0000-\u0020]/.test(destination) ? destination : null;
    return [{ id: String(item.id), title: item.title, body: text(item.body), createdAt: text(item.createdAt), link: safeLink }];
  }).slice(0, 4);
}

function useDashboardActivity(scope: string, enabled: boolean) {
  const [attempt, setAttempt] = useState(0);
  const [activity, setActivity] = useState<ReadState<Activity[]>>({ scope, status: "loading", data: null });
  useEffect(() => {
    if (!enabled) return;
    let current = true;
    setActivity({ scope, status: "loading", data: null });
    void api.getNotifications().then((result: unknown) => {
      const data = notificationRows(result);
      if (current) setActivity({ scope, status: "ready", data });
    }).catch(() => { if (current) setActivity({ scope, status: "error", data: null }); });
    return () => { current = false; };
  }, [scope, attempt, enabled]);
  return {
    activity: activity.scope === scope ? activity : { scope, status: "loading" as const, data: null },
    retry: () => setAttempt((value) => value + 1),
  };
}

function Panel({ id, title, icon, action, children }: { id?: string; title: string; icon: ReactNode; action?: ReactNode; children: ReactNode }) {
  return <section id={id} className="dashboard-panel"><div className="dashboard-panel-heading"><h2>{icon}{title}</h2>{action}</div>{children}</section>;
}

function ReadMessage({ status, retry }: { status: "loading" | "error"; retry: () => void }) {
  const { t } = useI18n();
  return <p className={status === "error" ? "dashboard-read-error" : "dashboard-subtle"} role="status">{status === "loading" ? t("common.loading", "Loading…") : <>{t("dashboard.unavailable", "Couldn’t load this data.")} {!isNativeApp() && <button type="button" onClick={retry}>{t("common.retry", "Retry")}</button>}</>}</p>;
}

function CompletionPanel({ completion, enabled, userId }: { completion: DashboardCompletion | null; enabled: boolean; userId: number }) {
  const { t } = useI18n();
  const percent = completion ? Math.max(0, Math.min(100, Math.round(completion.completeness))) : null;
  const labels: Record<string, string> = { headline: "Bio", city: "Location", primaryRole: "Primary role", yearsExperience: "Years of experience" };
  const missing = completion?.missing ?? [];
  return <Panel title={t("dashboard.completion.title", "Profile setup")} icon={<Sparkles size={17} />}>
    <div className="dashboard-completion-summary">
      <div className="dashboard-completion-ring" style={{ background: percent === null ? "var(--dash-line)" : `conic-gradient(var(--dash-green) ${percent}%, var(--dash-line) 0)` }}><span>{percent === null ? "—" : `${percent}%`}</span></div>
      <div><strong>{percent === 100 ? t("dashboard.profileComplete", "Profile setup complete") : t(completion?.onboarded ? "dashboard.profile.complete" : "dashboard.profile.setup")}</strong><p>{t("dashboard.completion.help", "Jobseeker requirements are checked separately.")}</p></div>
    </div>
    {enabled && !completion && <p className="dashboard-subtle">{t("dashboard.completionUnavailable", "Open your profile to check setup.")}</p>}
    <div className="dashboard-setup-list">
      <Link href="/worker-profile"><CheckCircle2 size={15} className={percent === 100 ? "dashboard-success" : ""} /><span>{t("dashboard.profileDetails", "Profile details")}</span><small>{percent === 100 ? t("common.completed", "Completed") : t("common.edit", "Edit")}</small><ChevronRight size={14} /></Link>
      {missing.slice(0, 3).map((field) => <Link key={field} href="/worker-profile"><Plus size={15} /><span>{t(`dashboard.setup.${field}`, labels[field] || field.replace(/([A-Z])/g, " $1"))}</span><small>{t("common.add", "Add")}</small><ChevronRight size={14} /></Link>)}
      <Link href={`/u/${userId}?view=public`}><UserRound size={15} /><span>{t("dashboard.publicProfile", "View public profile")}</span><ChevronRight size={14} /></Link>
    </div>
  </Panel>;
}

type Props = {
  activityState?: ReadState<Activity[]>;
  onActivityRetry?: () => void;
  scope: string;
  userId: number;
  profile: unknown;
  completion: DashboardCompletion | null;
  workerEnabled: boolean;
  section: DashboardProfileSection;
  legacyMobile?: boolean;
  overviewSummary: ReactNode;
  activities: ReactNode;
  children: ReactNode;
};

export function DashboardOverview(props: Props) {
  const { t, lang } = useI18n();
  const fallback = useDashboardActivity(props.scope, !props.legacyMobile && !props.activityState);
  const activity = props.activityState ?? fallback.activity;
  const retry = props.onActivityRetry ?? fallback.retry;
  const profile = record(props.profile);
  const skills = Array.isArray(profile.skills) ? profile.skills.filter((skill: unknown): skill is string => typeof skill === "string") : [];
  // An unresolved completion read is not evidence of a completed profile.
  const complete = props.completion !== null && Number.isFinite(props.completion.completeness) && props.completion.completeness >= 100;
  if (props.legacyMobile) return <>{props.children}</>;
  return <div className="dashboard-reference-content dashboard-columns">
    <div id="dashboard-profile-panel" className="dashboard-reference-center" role="tabpanel" aria-labelledby={`dashboard-profile-tab-${props.section}`} tabIndex={0}>
      {props.section === "overview" && <>
        {props.overviewSummary}
        <Panel id="dashboard-about" title={t("profile.about", "About")} icon={<UserRound size={17} />} action={<Link href="/worker-profile">{t("common.edit", "Edit")}</Link>}>
          <p className="dashboard-about-copy">{text(profile.bio) || t("dashboard.addBio", "Tell clients about your experience and the work you do.")}</p>
        </Panel>
      </>}
      {props.children}
    </div>
    <aside className="dashboard-right-sidebar" aria-label={t("dashboard.profileTools", "Profile tools and activity")} tabIndex={0}>
      {!complete && <CompletionPanel completion={props.completion} enabled={props.workerEnabled} userId={props.userId} />}
      <Panel id="dashboard-activity" title={t("dashboard.recentActivity", "Recent activity")} icon={<Clock size={17} />}>
        {activity.status !== "ready" ? <ReadMessage status={activity.status} retry={retry} /> : activity.data?.length ? <div className="dashboard-activity-list">{activity.data.map((item) => {
          const content = <><span className="dashboard-activity-icon"><CheckCircle2 size={15} /></span><span><strong>{item.title}</strong><p>{item.body}</p>{Number.isFinite(Date.parse(item.createdAt)) && <time dateTime={item.createdAt}>{new Date(item.createdAt).toLocaleDateString(lang === "tl" ? "fil-PH" : "en-PH", { month: "short", day: "numeric" })}</time>}</span></>;
          return item.link ? <Link key={item.id} href={item.link}>{content}</Link> : <div key={item.id}>{content}</div>;
        })}</div> : <p className="dashboard-empty">{t("dashboard.noActivity", "No recent activity yet.")}</p>}
      </Panel>
      <Panel id="dashboard-skills" title={t("profile.skills", "Skills")} icon={<List size={17} />} action={<Link href="/worker-profile">{t("common.edit", "Edit")}</Link>}><div className="dashboard-skill-list">{skills.length ? skills.map((skill) => <span key={skill}>{skill}</span>) : <p className="dashboard-subtle">{t("dashboard.addSkills", "Add your skills to help clients find you.")}</p>}</div></Panel>
      {props.activities}
    </aside>
  </div>;
}
